import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { CssBaseline, ThemeProvider, Snackbar, Alert, alpha } from '@mui/material';
import AppRoutes from './routes';
import { authApi, setUnauthorizedHandler } from './api';
import { getTheme } from './theme';
import { useUIStore } from './store';
import { FullPageLoader } from './routes/RouteGuards';
import ConfirmDialog from './components/feedback/ConfirmDialog';

const App = () => {
  const [session, setSession] = useState(null);
  const [translation, setTranslation] = useState({});
  const [lang, setLang] = useState('en');
  const [isLoading, setIsLoading] = useState(true);
  const themeMode = useUIStore((state) => state.themeMode);
  const snackbar = useUIStore((state) => state.snackbar);
  const hideSnackbar = useUIStore((state) => state.hideSnackbar);
  const theme = useMemo(() => getTheme(themeMode), [themeMode]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      localStorage.removeItem('token');
      localStorage.removeItem('session');
      setSession(null);
    });

    return () => setUnauthorizedHandler(null);
  }, []);

  useEffect(() => {
    const SESSION_TIMEOUT_MS = 8000;

    // Initialize session from server or localStorage
    const initializeSession = async () => {
      try {
        const response = await Promise.race([
          authApi.getSession(),
          new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Session request timed out')), SESSION_TIMEOUT_MS);
          }),
        ]);
        const payload = response.data || {};
        const sessionData = payload.session || payload.data?.user || payload.user || null;
        const translationData = payload.translation || payload.data?.translation || {};
        const langValue = payload.lang || payload.data?.lang || 'en';

        setSession(sessionData);
        if (sessionData) {
          localStorage.setItem('session', JSON.stringify(sessionData));
        } else {
          localStorage.removeItem('session');
        }
        setTranslation(translationData);
        setLang(langValue);
      } catch (error) {
        console.error('Failed to fetch session:', error);
        const status = error?.response?.status;
        if (status === 401) {
          // Unauthorized - clear session; ProtectedRoute will handle redirect for private screens
          localStorage.removeItem('token');
          localStorage.removeItem('session');
          setSession(null);
        } else {
          // For 404 or network errors, fallback to localStorage
          // but only if we have a valid token
          const token = localStorage.getItem('token');
          const storedSession = localStorage.getItem('session');
          if (token && storedSession) {
            try {
              setSession(JSON.parse(storedSession));
            } catch {
              localStorage.removeItem('session');
              setSession(null);
            }
          } else {
            // No token means not logged in
            setSession(null);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeSession();
  }, []);

  useEffect(() => {
    if (!isLoading) return undefined;
    const fallbackTimer = setTimeout(() => {
      setIsLoading(false);
    }, 10000);
    return () => clearTimeout(fallbackTimer);
  }, [isLoading]);

  if (isLoading) {
    return <FullPageLoader message="" />;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AppRoutes
          session={session}
          translation={translation}
          lang={lang}
          isLoading={isLoading}
        />
      </BrowserRouter>
      <ConfirmDialog />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.duration}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={hideSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={(theme) => ({
            width: '100%',
            borderRadius: 2.5,
            border: '1px solid transparent',
            ...(snackbar.severity === 'success' && {
              bgcolor: '#ecf8f4',
              color: '#0f2f2a',
              borderColor: '#b7ddd2',
              backgroundImage:
                'linear-gradient(135deg, #f7fffb 0%, #e6f6f1 55%, #dff1ec 100%)',
              boxShadow: `0 12px 30px ${alpha('#0b3b33', 0.16)}`,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 6,
                borderTopLeftRadius: 12,
                borderBottomLeftRadius: 12,
                bgcolor: '#10b981',
              },
              '& .MuiAlert-icon': {
                color: '#0f8f76',
                bgcolor: '#d8f3ea',
                borderRadius: '50%',
                padding: '6px',
                marginRight: '10px',
              },
              '& .MuiAlert-action': { color: '#5f726c' },
              '& .MuiAlert-message': { color: '#0f2f2a', fontWeight: 600 },
            }),
            ...(snackbar.severity === 'error' && {
              bgcolor: '#e5484d',
              color: '#ffffff',
              borderColor: '#b9363b',
              boxShadow: `0 10px 24px ${alpha('#7f1d1d', 0.28)}`,
              '& .MuiAlert-icon': { color: '#ffffff' },
              '& .MuiAlert-action': { color: '#ffffff' },
              '& .MuiAlert-message': { color: '#ffffff', fontWeight: 600 },
            }),
          })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default App;
